import React, {useContext, useEffect} from 'react'
import styled from 'styled-components'
import { OfficeTsuikaKanyushaData } from '../../../../types/formData'
import { Heading } from '../../../ui/Heading'
import { Input } from '../../../ui/Input'
import { DatePicker } from '../../../ui/DatePicker'
import { Controller, useFormContext } from 'react-hook-form'
import { MessageColumnSection } from '../../../layout/inputParts/MessageColumnSection'
import { mediaQuery } from '../../../../themes/size'
import {
  isMemberId,
  isPhoneNumber,
  isPostalCode,
  isYYYYMMDD,
  isZenKatakana,
} from '../../../../util/validation'
import moment from 'moment'
import { CheckBox } from '../../../ui/CheckBox'
import { API, CONFIG } from '../../../../const'
import { fetchAddress } from '../../../../util/fetchAddressByPostalCode'
import { AdminAuthContext } from '../../../../auth/AdminAuthProvider'
import {AdminApplication} from "../../../../types/application";
import {ApplicantSection} from "./inputParts";

type Props = {
  disabled: boolean
  canAdminUpload?: boolean
  isUploading?: boolean
  shouldShowOtherFormLinks?: true
} & ({
  isAdmin: true
  application: AdminApplication
} | {
  isAdmin: false
  application: undefined
})

const Component: React.FC<Props> = (props) => {
  const methods = useFormContext<OfficeTsuikaKanyushaData['formData']>()
  const { control, errors } = methods
  const { bearerToken } = useContext(AdminAuthContext)

  const officeName = methods.watch('officeInfo.name')
  useEffect(() => {
    if (!props.disabled) {
      methods.setValue('officeName', officeName)
    }
  }, [methods, officeName, props.disabled]);

  const memberId = methods.watch('memberId')
  const [setMemberId, setSetMemberId] = React.useState(false)
  if (props.isAdmin && !setMemberId) {
    methods.setValue('memberId', `${props.application?.applicantId}-`)
    memberId && setSetMemberId(true)
  }

  const isMemberIdUnique = async (memberId: string) => {
    const apiPath = `${API.ADMIN_MEMBERS}/${memberId}`
    return fetch(apiPath, {
      headers: { Authorization: 'Bearer ' + bearerToken },
      mode: 'cors',
    }).then((res) => !res.ok) // not_ok(not 200) === not exists === unique(new one)
  }

  return (
    <>
      <HeadingArea>
        <Heading type="screenTitle" tag="h1">
          ▼追加加入者手続き
        </Heading>
        {!props.isAdmin && (
          <Paragraph>
            下記の項目をすべて入力し利用申請を行ってください。
            <br />
            内容に問題がなかった場合は、後日、ステータスが承認済みになります。
            <br />
            また、申請内容について事務局から確認させていただく場合もございます。
          </Paragraph>
        )}
      </HeadingArea>

      {Object.keys(methods.errors).length > 0 && (
        <Section>
          <ErrorMessage>
            入力内容に不備があります。各入力項目のメッセージを参照してください。
          </ErrorMessage>
        </Section>
      )}

      <ApplicantSection disabled={props.disabled} />

      <Section>
        <Heading type="sectionTitle" tag="h2">
          ■申請情報
        </Heading>

        {props.isAdmin && (
          <>
            <InputField>
              <Heading type="blockTitle" tag="span">
                会員番号
              </Heading>
              <Controller
                control={control}
                name="memberId"
                rules={{
                  validate: {
                    isMemberId,
                    startWithOfficeId: (value: string) =>
                      value.startsWith(props.application.applicantId),
                    duplicateMemberId: isMemberIdUnique,
                  },
                }}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    type="text"
                    defaultValue={value}
                    onChange={onChange}
                    disabled={['accepted', 'rejected'].includes(props.application.approvalStatus)}
                    onBlurCapture={onBlur}
                    placeholder="1234-567"
                  />
                )}
              />
            </InputField>
            {errors.memberId && (
              <ErrorMessage>{errors.memberId.message}</ErrorMessage>
            )}
            {errors.memberId?.type === 'isMemberId' && (
              <ErrorMessage>「{props.application.applicantId}-123」の形式で入力してください</ErrorMessage>
            )}
            {errors.memberId?.type === 'startWithOfficeId' && (
              <ErrorMessage>上4桁は当該事業所ID({props.application.applicantId})を指定してください</ErrorMessage>
            )}
            {errors.memberId?.type === 'duplicateMemberId' && (
              <ErrorMessage>
                すでに使用済みの会員IDです。下3桁を変更してください
              </ErrorMessage>
            )}
          </>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            事業所名
          </Heading>
          <Controller
            control={control}
            name="officeName"
            rules={{ required: '事業所名が正しく補完されませんでした。しばらく時間をおいていただいてから再度申請をお願いいたします。' }}
            render={({value, onChange}) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={onChange}
                disabled
              />
            )}
          />
        </InputField>
        {errors.officeName && (
          <ErrorMessage>{errors.officeName.message}</ErrorMessage>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            会員氏名
          </Heading>
          <Controller
            control={control}
            name="name"
            rules={{ required: '入力してください' }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
                placeholder="文京　太郎"
              />
            )}
          />
        </InputField>
        {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

        <InputField>
          <Heading type="blockTitle" tag="span">
            フリガナ
          </Heading>
          <Controller
            control={control}
            name="nameKana"
            rules={{
              required: '入力してください',
              validate: { isZenKatakana },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
                placeholder="ブンキョウ　タロウ"
              />
            )}
          />
        </InputField>
        {errors.nameKana && (
          <ErrorMessage>{errors.nameKana.message}</ErrorMessage>
        )}
        {errors.nameKana?.type === 'isZenKatakana' && (
          <ErrorMessage>全角カタカナで入力してください</ErrorMessage>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            生年月日
          </Heading>
          <Controller
            control={control}
            name="birthDay"
            rules={{
              required: '入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlur={onBlur}
                placeholder="1980/01/01"
              />
            )}
          />
        </InputField>
        {errors.birthDay && (
          <ErrorMessage>{errors.birthDay.message}</ErrorMessage>
        )}
        {errors.birthDay?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            郵便番号
          </Heading>
          <Controller
            control={control}
            name="postalCode"
            rules={{
              required: '入力してください',
              validate: { isPostalCode }
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={async (e) => {
                  const inputPostalCode = e.target.value
                  if (inputPostalCode.length === 8) {
                    const address = await fetchAddress(inputPostalCode)
                    if (address) {
                      methods.setValue('address', address, {
                        shouldDirty: true,
                      })
                    }
                  }
                  return onChange(e)
                }}
                disabled={props.disabled}
                onBlurCapture={onBlur}
                placeholder="112-0003"
              />
            )}
          />
        </InputField>
        {errors.postalCode && (
          <ErrorMessage>{errors.postalCode.message}</ErrorMessage>
        )}
        {errors.postalCode?.type === 'isPostalCode' && (
          <ErrorMessage>半角数字+半角ハイフンで入力してください</ErrorMessage>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            会員住所
          </Heading>
          <Controller
            control={control}
            name="address"
            rules={{ required: '入力してください' }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                value={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
                placeholder="東京都文京区春日1-16-21"
              />
            )}
          />
        </InputField>
        {errors.address && (
          <ErrorMessage>{errors.address.message}</ErrorMessage>
        )}


        <InputField>
          <Heading type="blockTitle" tag="span">
            電話番号
          </Heading>
          <Controller
            control={control}
            name="tel"
            rules={{
              required: '入力してください',
              validate: { isPhoneNumber },
            }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
                placeholder="0358031108"
              />
            )}
          />
        </InputField>
        {errors.tel && (
          <ErrorMessage>{errors.tel.message}</ErrorMessage>
        )}
        {errors.tel?.type === 'isPhoneNumber' && (
          <ErrorMessage>半角数字10桁か11桁で入力してください</ErrorMessage>
        )}

        {props.isAdmin && (
          <Section>
            <Heading type="sectionTitle" tag="h2">
              ■利用状況
            </Heading>
          </Section>
        )}

        <InputField>
          <Heading type="blockTitle" tag="span">
            入会日
          </Heading>
          <Controller
            control={control}
            name="joinDate"
            rules={{
              required: '今月か来月の日付を入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlur={onBlur}
                placeholder="2024/01/01"
                minDate={moment().toDate()}
                maxDate={moment().add(1, 'month').endOf('month').toDate()}
              />
            )}
          />
        </InputField>
        {errors.joinDate && (
          <ErrorMessage>{errors.joinDate.message}</ErrorMessage>
        )}
        {errors.joinDate?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}

        {!props.isAdmin && (
          <>
            <CheckBoxWrapper>
              <Controller
                control={control}
                name="privacyPolicyAgreement"
                rules={{
                  required: '確認の上、チェックを入れてください',
                  validate: { isTrue: (value) => value === true },
                }}
                render={({ value }) => (
                  <CheckBox
                    disabled={props.disabled}
                    id="privacy"
                    value={value}
                    checked={!!value}
                    onChange={() => {
                      methods.setValue('privacyPolicyAgreement', !value)
                    }}
                  />
                )}
              />
              <CheckBoxLabel htmlFor="privacy">
                個人情報の取扱について同意する
              </CheckBoxLabel>
            </CheckBoxWrapper>
            <AnchorLink href={CONFIG.PRIVACY_URL} target="_blank">
              個人情報保護について
            </AnchorLink>
            {errors.privacyPolicyAgreement && (
              <ErrorMessage>
                {errors.privacyPolicyAgreement.message}
              </ErrorMessage>
            )}
            {errors.privacyPolicyAgreement?.type === 'isTrue' && (
              <ErrorMessage>確認の上、チェックを入れてください</ErrorMessage>
            )}
          </>
        )}
      </Section>

      <MessageColumnSection disabled={props.disabled} />
    </>
  )
}

const HeadingArea = styled.div`
  margin-top: 32px;
`
export const Section = styled.section`
  margin-top: 72px;
`
export const InputField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 510px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;

    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const ErrorMessage = styled.div`
  color: red;
`
const Paragraph = styled.p`
  margin-top: 24px;
  font-size: 16px;
`
const CheckBoxWrapper = styled.div`
  margin-top: 16px;
`
const CheckBoxLabel = styled.label`
  cursor: pointer;
  margin-left: 8px;
  vertical-align: 0.15em;
`
const AnchorLink = styled.a`
  color: #439a89;
  &:hover {
    text-decoration: none;
  }
`

export default Component
